(() => {
  const pad = (value) => String(value).padStart(2, '0');

  function initCountdown(wrap) {
    const targetValue = wrap.dataset.target;
    const output = wrap.querySelector('[data-pp-countdown]');
    if (!targetValue || !output) return;

    const parseCountdownTarget = (value) => {
      const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);

      if (dateOnly) {
        const [, year, month, day] = dateOnly;
        return new Date(
          Number(year),
          Number(month) - 1,
          Number(day),
          23,
          59,
          59,
          999
        );
      }

      return new Date(value);
    };

    const target = parseCountdownTarget(targetValue);
    if (Number.isNaN(target.getTime())) return;

    const days = output.querySelector('[data-days]');
    const hours = output.querySelector('[data-hours]');
    const minutes = output.querySelector('[data-minutes]');
    const seconds = output.querySelector('[data-seconds]');
    const expired = wrap.querySelector('[data-pp-expired]');

    const update = () => {
      const distance = target.getTime() - Date.now();

      if (distance <= 0) {
        output.hidden = true;

        if (expired) {
          expired.textContent = wrap.dataset.expired || '';
          expired.hidden = !expired.textContent;
        }

        return false;
      }

      days.textContent = pad(Math.floor(distance / 86400000));
      hours.textContent = pad(
        Math.floor((distance % 86400000) / 3600000)
      );
      minutes.textContent = pad(
        Math.floor((distance % 3600000) / 60000)
      );
      seconds.textContent = pad(
        Math.floor((distance % 60000) / 1000)
      );

      return true;
    };

    update();

    const timer = window.setInterval(() => {
      if (!update()) window.clearInterval(timer);
    }, 1000);

    wrap._ppCountdownTimer = timer;
  }

  function initSlider(section) {
    const slider = section.querySelector('[data-pp-slider]');
    if (!slider) return;

    const slides = Array.from(
      slider.querySelectorAll('[data-pp-slide]')
    );
    const dots = Array.from(
      slider.querySelectorAll('[data-pp-dot]')
    );

    const prev = slider.querySelector('[data-pp-prev]');
    const next = slider.querySelector('[data-pp-next]');
    const prevLabel = slider.querySelector(
      '[data-pp-prev-label]'
    );
    const nextLabel = slider.querySelector(
      '[data-pp-next-label]'
    );

    if (!slides.length) return;

    let index = Math.max(
      0,
      slides.findIndex((slide) =>
        slide.classList.contains('is-active')
      )
    );

    let autoplayTimer = null;
    let hasEnteredViewport = false;
    let isInViewport = false;
    let visibilityObserver = null;

    const show = (nextIndex, userInitiated = false) => {
      index =
        (nextIndex + slides.length) % slides.length;

      slides.forEach((slide, slideIndex) => {
        slide.classList.toggle(
          'is-active',
          slideIndex === index
        );

        slide.setAttribute(
          'aria-hidden',
          slideIndex === index ? 'false' : 'true'
        );
      });

      dots.forEach((dot, dotIndex) => {
        dot.classList.toggle(
          'is-active',
          dotIndex === index
        );

        dot.setAttribute(
          'aria-current',
          dotIndex === index ? 'true' : 'false'
        );
      });

      const previousSlideIndex =
        (index - 1 + slides.length) %
        slides.length;

      const followingSlideIndex =
        (index + 1) % slides.length;

      if (prevLabel) {
        prevLabel.textContent =
          slides[previousSlideIndex].dataset.slideLabel ||
          `Slide ${previousSlideIndex + 1}`;
      }

      if (nextLabel) {
        nextLabel.textContent =
          slides[followingSlideIndex].dataset.slideLabel ||
          `Slide ${followingSlideIndex + 1}`;
      }

      section.dispatchEvent(
        new CustomEvent('pp:slide:shown', {
          detail: { index }
        })
      );

      if (userInitiated) restartAutoplay();
    };

    const stopAutoplay = () => {
      if (autoplayTimer) {
        window.clearInterval(autoplayTimer);
        autoplayTimer = null;
      }
    };

    const restartAutoplay = () => {
      stopAutoplay();

      if (
        section.dataset.autoplay !== 'true' ||
        slides.length < 2 ||
        !hasEnteredViewport ||
        !isInViewport ||
        window.matchMedia(
          '(prefers-reduced-motion: reduce)'
        ).matches
      ) {
        return;
      }

      const speed =
        Number(section.dataset.autoplaySpeed) || 7000;

      autoplayTimer = window.setInterval(
        () => show(index + 1),
        speed
      );

      section._ppAutoplayTimer = autoplayTimer;
    };

    /*
     * RELEASE SLIDE NAVIGATION
     * ---------------------------------------------------------
     * IMPORTANT:
     *
     * Release slides are intentionally NOT swipeable.
     *
     * This prevents a horizontal swipe inside:
     * - margins
     * - blank areas
     * - product areas
     * - mobile content
     *
     * from unexpectedly changing the entire release and its
     * associated collection.
     *
     * Release changes now occur through the dedicated release
     * navigation controls only.
     * ---------------------------------------------------------
     */

    prev?.addEventListener('click', () => {
      show(index - 1, true);
    });

    next?.addEventListener('click', () => {
      show(index + 1, true);
    });

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const dotIndex = Number(dot.dataset.index);
        show(dotIndex, true);
      });
    });

    slider.addEventListener(
      'mouseenter',
      stopAutoplay
    );

    slider.addEventListener(
      'mouseleave',
      restartAutoplay
    );

    slider.addEventListener(
      'focusin',
      stopAutoplay
    );

    slider.addEventListener(
      'focusout',
      restartAutoplay
    );

    show(0);

    if ('IntersectionObserver' in window) {
      visibilityObserver =
        new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              isInViewport =
                entry.isIntersecting &&
                entry.intersectionRatio >= 0.2;

              if (
                isInViewport &&
                !hasEnteredViewport
              ) {
                hasEnteredViewport = true;
                show(0);
              }

              if (isInViewport) {
                restartAutoplay();
              } else {
                stopAutoplay();
              }
            });
          },
          {
            threshold: [0, 0.2, 0.5]
          }
        );

      visibilityObserver.observe(section);

      section._ppVisibilityObserver =
        visibilityObserver;
    } else {
      hasEnteredViewport = true;
      isInViewport = true;
      restartAutoplay();
    }
  }

  function removeThirdPartyWidgets(root) {
    if (!root) return;

    const phrases = [
      'notify me when available',
      'notify when available',
      'email me when available',
      'back in stock',
      'make an offer',
      'submit offer',
      'name your price',
      'request a price'
    ];

    root
      .querySelectorAll('[data-pp-product-card]')
      .forEach((card) => {
        card
          .querySelectorAll(
            'button, a, form, [role="button"], [class], [id]'
          )
          .forEach((element) => {
            if (
              element.matches(
                '.pp-product-card__button, ' +
                '.pp-product-card__icon-button, ' +
                '.pp-product-card__title, ' +
                '.pp-product-card__image, ' +
                '[data-pp-card-link], ' +
                '[data-pp-notify]'
              ) ||
              element.closest('.pp-quick-view')
            ) {
              return;
            }

            const text = (
              element.textContent || ''
            )
              .replace(/\s+/g, ' ')
              .trim()
              .toLowerCase();

            const signature =
              `${element.id || ''} ${
                element.className || ''
              }`.toLowerCase();

            const textMatch =
              text &&
              text.length <= 120 &&
              phrases.some((phrase) =>
                text.includes(phrase)
              );

            const signatureMatch = [
              'make-an-offer',
              'make_offer',
              'offer-button',
              'offer_btn',
              'notify-me',
              'notify_me',
              'back-in-stock',
              'back_in_stock',
              'bis-button',
              'restock-alert'
            ].some((token) =>
              signature.includes(token)
            );

            const unknownButton =
              element.tagName === 'BUTTON' &&
              !element.classList.contains(
                'pp-product-card__icon-button'
              ) &&
              !element.classList.contains(
                'pp-product-card__button--notify'
              );

            const appForm =
              element.tagName === 'FORM';

            if (
              textMatch ||
              signatureMatch ||
              unknownButton ||
              appForm
            ) {
              const removable =
                element.closest(
                  'form, ' +
                  '.shopify-app-block, ' +
                  '[data-app-block], ' +
                  '[data-bis-button], ' +
                  '[data-back-in-stock], ' +
                  '[data-notify-me]'
                ) || element;

              removable.style.setProperty(
                'display',
                'none',
                'important'
              );

              removable.setAttribute(
                'aria-hidden',
                'true'
              );
            }
          });
      });
  }

  function watchThirdPartyWidgets(section) {
    removeThirdPartyWidgets(section);

    const observer = new MutationObserver(() =>
      removeThirdPartyWidgets(section)
    );

    observer.observe(section, {
      childList: true,
      subtree: true
    });

    section._ppThirdPartyObserver = observer;
  }

  function updateCardVariant(card, input) {
    if (!card || !input) return;

    const priceWrap =
      card.querySelector('[data-pp-price]');

    const current =
      card.querySelector('[data-pp-current]');

    const compare =
      card.querySelector('[data-pp-compare]');

    const saleBadge =
      card.querySelector('[data-pp-sale-badge]');

    const soldOverlay =
      card.querySelector('[data-pp-sold-overlay]');

    const buyButton =
      card.querySelector('[data-pp-buy-button]');

    const notifyButton =
      card.querySelector('[data-pp-notify]');

    const image =
      card.querySelector('[data-pp-card-image]');

    const links =
      card.querySelectorAll('[data-pp-card-link]');

    const zeroPrice =
      Number(input.dataset.priceCents || 0) === 0;

    const sale =
      input.dataset.sale === 'true' &&
      !zeroPrice;

    const available =
      input.dataset.available === 'true';

    if (current) {
      current.textContent = zeroPrice
        ? 'Check for pricing...'
        : input.dataset.price || '';
    }

    if (compare) {
      compare.textContent = sale
        ? input.dataset.compare || ''
        : '';

      compare.hidden = !sale;
    }

    priceWrap?.classList.toggle(
      'is-sale',
      sale
    );

    priceWrap?.classList.toggle(
      'is-price-pending',
      zeroPrice
    );

    if (saleBadge) {
      saleBadge.hidden = !sale;
    }

    const comingSoon =
      card.dataset.comingSoon === 'true';

    if (soldOverlay) {
      soldOverlay.hidden =
        available && !comingSoon;
    }

    if (buyButton) {
      buyButton.hidden = !available;
    }

    if (notifyButton) {
      notifyButton.hidden = available;

      notifyButton.dataset.variantId =
        input.dataset.variantId ||
        input.value ||
        '';

      notifyButton.dataset.productId =
        input.dataset.productId ||
        card.dataset.productId ||
        '';
    }

    if (image && input.dataset.image) {
      image.src = input.dataset.image;
    }

    links.forEach((link) => {
      link.href =
        input.dataset.url ||
        card.dataset.productUrl ||
        '#';
    });

    card.classList.toggle(
      'pp-product-card--sold-out',
      !available
    );
  }

  function initProductCards(section) {
    section
      .querySelectorAll('[data-pp-product-card]')
      .forEach((card) => {
        card
          .querySelectorAll('[data-pp-variant]')
          .forEach((input) => {
            input.addEventListener(
              'change',
              () =>
                updateCardVariant(card, input)
            );
          });
      });
  }

  function initProductCarousels(section) {
    const carouselStates = [];

    section
      .querySelectorAll('[data-pp-products]')
      .forEach((wrap) => {
        const grid = wrap.querySelector(
          '[data-pp-product-grid]'
        );

        const prev = wrap.querySelector(
          '[data-pp-products-prev]'
        );

        const next = wrap.querySelector(
          '[data-pp-products-next]'
        );

        const progress = wrap.querySelector(
          '[data-pp-products-progress]'
        );

        if (!grid) return;

        let animationFrame = null;
        let isDragging = false;
        let startX = 0;
        let startY = 0;
        let startScroll = 0;
        let dragged = false;
        let dragAxis = null;

        const cardWidth = () => {
          const card =
            grid.querySelector(
              '.pp-product-card'
            );

          if (!card) {
            return Math.max(
              260,
              grid.clientWidth
            );
          }

          const styles =
            getComputedStyle(grid);

          const gap = parseFloat(
            styles.columnGap ||
            styles.gap ||
            0
          );

          return (
            card.getBoundingClientRect().width +
            gap
          );
        };

        const update = () => {
          const max = Math.max(
            0,
            grid.scrollWidth -
              grid.clientWidth
          );

          const ratio =
            max > 0
              ? grid.scrollLeft / max
              : 0;

          if (prev) {
            prev.disabled =
              max <= 3 ||
              grid.scrollLeft <= 3;

            prev.setAttribute(
              'aria-disabled',
              String(prev.disabled)
            );
          }

          if (next) {
            next.disabled =
              max <= 3 ||
              grid.scrollLeft >= max - 3;

            next.setAttribute(
              'aria-disabled',
              String(next.disabled)
            );
          }

          if (progress) {
            const viewportRatio =
              grid.scrollWidth > 0
                ? Math.min(
                    1,
                    grid.clientWidth /
                      grid.scrollWidth
                  )
                : 1;

            const scale = Math.max(
              0.16,
              viewportRatio
            );

            const availableTravel =
              Math.max(0, 1 - scale);

            progress.style.width =
              `${scale * 100}%`;

            progress.style.transform =
              `translateX(${
                (ratio *
                  availableTravel *
                  100) /
                scale
              }%)`;
          }
        };

        const animateScroll = (
          target,
          duration
        ) => {
          if (animationFrame) {
            cancelAnimationFrame(
              animationFrame
            );
          }

          const startPosition =
            grid.scrollLeft;

          const max = Math.max(
            0,
            grid.scrollWidth -
              grid.clientWidth
          );

          const endPosition = Math.max(
            0,
            Math.min(max, target)
          );

          const change =
            endPosition - startPosition;

          if (Math.abs(change) < 1) {
            update();
            return;
          }

          const startedAt =
            performance.now();

          const ease = (value) =>
            1 - Math.pow(1 - value, 3);

          const tick = (now) => {
            const elapsed =
              now - startedAt;

            const animationProgress =
              Math.min(
                1,
                elapsed / duration
              );

            grid.scrollLeft =
              startPosition +
              change *
                ease(
                  animationProgress
                );

            update();

            if (
              animationProgress < 1
            ) {
              animationFrame =
                requestAnimationFrame(
                  tick
                );
            }
          };

          animationFrame =
            requestAnimationFrame(tick);
        };

        const move = (direction) => {
          update();

          const cards = Math.max(
            1,
            Number(
              wrap.dataset.scrollCards
            ) || 1
          );

          const duration = Math.max(
            150,
            Number(
              wrap.dataset.scrollDuration
            ) || 450
          );

          const distance =
            cardWidth() * cards;

          animateScroll(
            grid.scrollLeft +
              direction * distance,
            duration
          );
        };

        prev?.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            event.stopImmediatePropagation();
            move(-1);
          }
        );

        next?.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            event.stopImmediatePropagation();
            move(1);
          }
        );

        grid.addEventListener(
          'dragstart',
          (event) => {
            event.preventDefault();
          }
        );

        grid
          .querySelectorAll('img, a')
          .forEach((element) => {
            element.setAttribute(
              'draggable',
              'false'
            );
          });

        /*
         * PRODUCT CAROUSEL SWIPING
         * -----------------------------------------------------
         * Product cards remain independently swipeable.
         *
         * Horizontal movement here scrolls ONLY the product
         * carousel. It does not change the parent release.
         * -----------------------------------------------------
         */

        grid.addEventListener(
          'pointerdown',
          (event) => {
            if (
              event.pointerType ===
                'mouse' &&
              event.button !== 0
            ) {
              return;
            }

            const interactive =
              event.target.closest(
                'button, input, select, textarea, label, ' +
                '[role="button"], ' +
                '.pp-product-card__variants, ' +
                '.pp-product-card__title, ' +
                '.pp-product-card__button, ' +
                'a:not(.pp-product-card__image)'
              );

            const imageLink =
              event.target.closest(
                '.pp-product-card__image'
              );

            if (
              interactive &&
              !imageLink
            ) {
              dragged = false;
              isDragging = false;

              grid.classList.remove(
                'is-dragging'
              );

              return;
            }

            isDragging = true;
            dragged = false;
            dragAxis = null;

            startX = event.clientX;
            startY = event.clientY;
            startScroll =
              grid.scrollLeft;

            try {
              grid.setPointerCapture(
                event.pointerId
              );
            } catch (_) {}
          }
        );

        grid.addEventListener(
          'pointermove',
          (event) => {
            if (!isDragging) return;

            const distanceX =
              event.clientX - startX;

            const distanceY =
              event.clientY - startY;

            if (!dragAxis) {
              if (
                Math.abs(distanceX) <
                  12 &&
                Math.abs(distanceY) <
                  12
              ) {
                return;
              }

              if (
                Math.abs(distanceY) >=
                Math.abs(distanceX) *
                  1.15
              ) {
                dragAxis = 'vertical';
                isDragging = false;

                grid.classList.remove(
                  'is-dragging'
                );

                try {
                  grid.releasePointerCapture(
                    event.pointerId
                  );
                } catch (_) {}

                return;
              }

              if (
                Math.abs(distanceX) >
                Math.abs(distanceY) *
                  1.35
              ) {
                dragAxis =
                  'horizontal';

                grid.classList.add(
                  'is-dragging'
                );
              } else {
                return;
              }
            }

            if (
              dragAxis !==
              'horizontal'
            ) {
              return;
            }

            if (
              Math.abs(distanceX) > 14
            ) {
              dragged = true;
            }

            grid.scrollLeft =
              startScroll - distanceX;

            update();
          }
        );

        const endDrag = (event) => {
          if (!isDragging) return;

          isDragging = false;
          dragAxis = null;

          grid.classList.remove(
            'is-dragging'
          );

          try {
            grid.releasePointerCapture(
              event.pointerId
            );
          } catch (_) {}

          update();
        };

        grid.addEventListener(
          'pointerup',
          endDrag
        );

        grid.addEventListener(
          'pointercancel',
          endDrag
        );

        window.addEventListener(
          'pointerup',
          endDrag
        );

        grid.addEventListener(
          'click',
          (event) => {
            if (!dragged) return;

            const imageLink =
              event.target.closest(
                '.pp-product-card__image'
              );

            const nonImageInteractive =
              event.target.closest(
                'button, input, select, textarea, label, ' +
                '[role="button"], ' +
                '.pp-product-card__variants, ' +
                '.pp-product-card__title, ' +
                '.pp-product-card__button, ' +
                'a:not(.pp-product-card__image)'
              );

            if (
              nonImageInteractive &&
              !imageLink
            ) {
              dragged = false;
              return;
            }

            event.preventDefault();
            event.stopPropagation();

            dragged = false;
          },
          true
        );

        grid
          .querySelectorAll(
            '[data-pp-card-link]'
          )
          .forEach((link) => {
            link.addEventListener(
              'click',
              (event) => {
                if (dragged) return;

                const href =
                  link.getAttribute(
                    'href'
                  );

                if (
                  !href ||
                  href === '#'
                ) {
                  return;
                }

                event.stopPropagation();
              }
            );
          });

        grid.addEventListener(
          'scroll',
          update,
          { passive: true }
        );

        const resizeObserver =
          'ResizeObserver' in window
            ? new ResizeObserver(() =>
                requestAnimationFrame(
                  update
                )
              )
            : null;

        resizeObserver?.observe(grid);

        wrap._ppProductResizeObserver =
          resizeObserver;

        carouselStates.push({
          wrap,
          grid,
          update
        });

        requestAnimationFrame(() =>
          requestAnimationFrame(update)
        );
      });

    section._ppProductCarousels =
      carouselStates;

    section.addEventListener(
      'pp:slide:shown',
      () => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            carouselStates.forEach(
              ({ update }) => update()
            );
          });
        });
      }
    );
  }

  function initQuickView(section) {
    const modal = section.querySelector(
      '[data-pp-quick-view]'
    );

    if (!modal) return;

    const image = modal.querySelector(
      '[data-pp-qv-image]'
    );

    const title = modal.querySelector(
      '[data-pp-qv-title]'
    );

    const variants = modal.querySelector(
      '[data-pp-qv-variants]'
    );

    const price = modal.querySelector(
      '[data-pp-qv-price]'
    );

    const link = modal.querySelector(
      '[data-pp-qv-link]'
    );

    let returnFocus = null;

    const close = () => {
      modal.hidden = true;

      document.body.classList.remove(
        'pp-quick-view-open'
      );

      returnFocus?.focus?.();
    };

    const renderPrice = (input) => {
      if (!input || !price) return;

      const zeroPrice =
        Number(
          input.dataset.priceCents || 0
        ) === 0;

      const sale =
        input.dataset.sale === 'true' &&
        !zeroPrice;

      price.classList.toggle(
        'is-sale',
        sale
      );

      price.classList.toggle(
        'is-price-pending',
        zeroPrice
      );

      price.innerHTML = zeroPrice
        ? '<strong>Check for pricing...</strong>'
        : `${
            sale
              ? `<s>${
                  input.dataset.compare ||
                  ''
                }</s>`
              : ''
          }<strong>${
            input.dataset.price || ''
          }</strong>`;

      if (
        image &&
        input.dataset.image
      ) {
        image.src =
          input.dataset.image;
      }

      if (link) {
        link.href =
          input.dataset.url || '#';
      }
    };

    const open = (button) => {
      const card = button.closest(
        '[data-pp-product-card]'
      );

      if (!card) return;

      returnFocus = button;

      const selected =
        card.querySelector(
          '[data-pp-variant]:checked'
        ) ||
        card.querySelector(
          '[data-pp-variant]'
        );

      const cardImage =
        card.querySelector(
          '[data-pp-card-image]'
        );

      if (title) {
        title.textContent =
          card.dataset.productTitle ||
          '';
      }

      if (image) {
        image.src =
          selected?.dataset.image ||
          cardImage?.src ||
          '';

        image.alt =
          card.dataset.productTitle ||
          '';
      }

      if (link) {
        link.href =
          selected?.dataset.url ||
          card.dataset.productUrl ||
          '#';
      }

      if (variants) {
        variants.innerHTML = '';

        const sourceGroup =
          card.querySelector(
            '[data-pp-variant-group]'
          );

        if (sourceGroup) {
          const clone =
            sourceGroup.cloneNode(true);

          clone
            .querySelectorAll('input')
            .forEach((input) => {
              input.name =
                `pp-qv-${Date.now()}`;

              input.addEventListener(
                'change',
                () =>
                  renderPrice(input)
              );
            });

          variants.appendChild(clone);
          variants.hidden = false;
        } else {
          variants.hidden = true;
        }
      }

      if (selected) {
        renderPrice(selected);
      } else if (price) {
        const cardPrice =
          card.querySelector(
            '[data-pp-price]'
          );

        price.innerHTML =
          cardPrice?.innerHTML || '';

        price.classList.toggle(
          'is-sale',
          cardPrice?.classList.contains(
            'is-sale'
          )
        );
      }

      modal.hidden = false;

      document.body.classList.add(
        'pp-quick-view-open'
      );

      modal
        .querySelector(
          '.pp-quick-view__close'
        )
        ?.focus();
    };

    section
      .querySelectorAll(
        '[data-pp-quick-open]'
      )
      .forEach((button) => {
        button.addEventListener(
          'click',
          () => open(button)
        );
      });

    modal
      .querySelectorAll(
        '[data-pp-qv-close]'
      )
      .forEach((button) =>
        button.addEventListener(
          'click',
          close
        )
      );

    document.addEventListener(
      'keydown',
      (event) => {
        if (
          event.key === 'Escape' &&
          !modal.hidden
        ) {
          close();
        }
      }
    );
  }

  function initRestockButtons(section) {
    section
      .querySelectorAll(
        '[data-pp-notify]'
      )
      .forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            const detail = {
              source:
                'plug-home-slide',

              productId:
                button.dataset.productId ||
                '',

              variantId:
                button.dataset.variantId ||
                '',

              productTitle:
                button.dataset
                  .productTitle || '',

              button
            };

            const event =
              new CustomEvent(
                'plug:restock:open',
                {
                  bubbles: true,
                  cancelable: true,
                  detail
                }
              );

            const handled =
              !button.dispatchEvent(
                event
              );

            if (!handled) {
              const card =
                button.closest(
                  '[data-pp-product-card]'
                );

              const selected =
                card?.querySelector(
                  '[data-pp-variant]:checked'
                );

              const fallbackUrl =
                selected?.dataset.url ||
                card?.dataset
                  .productUrl;

              if (fallbackUrl) {
                window.location.href =
                  fallbackUrl;
              }
            }
          }
        );
      });
  }

  /*
   * ANNOUNCEMENT BAR
   * ---------------------------------------------------------
   * The announcement remains a single line.
   *
   * On mobile, JS checks whether the content actually
   * overflows the available viewport width.
   *
   * If it does, the marquee class is enabled.
   * If it fits, the announcement remains stationary.
   *
   * The Liquid/CSS side should contain:
   *
   * [data-pp-announcement]
   * .pp-preorders__announcement-viewport
   * [data-pp-announcement-track]
   * .pp-preorders__announcement-item
   *
   * ---------------------------------------------------------
   */

  function initAnnouncement(section) {
    const announcement =
      section.querySelector(
        '[data-pp-announcement]'
      );

    const viewport =
      announcement?.querySelector(
        '.pp-preorders__announcement-viewport'
      );

    const track =
      announcement?.querySelector(
        '[data-pp-announcement-track]'
      );

    const firstItem =
      track?.querySelector(
        '.pp-preorders__announcement-item:not(.pp-preorders__announcement-item--clone)'
      );

    if (
      !announcement ||
      !viewport ||
      !track ||
      !firstItem
    ) {
      return;
    }

    const update = () => {
      track.classList.remove(
        'is-scrolling'
      );

      track.style.removeProperty(
        '--pp-announcement-duration'
      );

      /*
       * Desktop stays stationary.
       * Mobile gets marquee behavior only
       * when the line cannot fit.
       */
      if (
        window.matchMedia(
          '(min-width: 750px)'
        ).matches
      ) {
        return;
      }

      const available =
        viewport.clientWidth;

      const needed =
        firstItem.scrollWidth;

      if (
        needed <= available + 2
      ) {
        return;
      }

      /*
       * Keep the ticker speed visually
       * consistent regardless of text
       * length.
       */
      const pixelsPerSecond = 34;

      const duration = Math.max(
        10,
        Math.min(
          24,
          needed /
            pixelsPerSecond
        )
      );

      track.style.setProperty(
        '--pp-announcement-duration',
        `${duration}s`
      );

      track.classList.add(
        'is-scrolling'
      );
    };

    requestAnimationFrame(update);

    const resizeObserver =
      'ResizeObserver' in window
        ? new ResizeObserver(() =>
            requestAnimationFrame(
              update
            )
          )
        : null;

    resizeObserver?.observe(
      viewport
    );

    resizeObserver?.observe(
      firstItem
    );

    section._ppAnnouncementResizeObserver =
      resizeObserver;
  }

  function initSection(section) {
    section
      .querySelectorAll(
        '[data-pp-countdown-wrap]'
      )
      .forEach(initCountdown);

    initAnnouncement(section);
    initSlider(section);
    initProductCards(section);
    initProductCarousels(section);
    initQuickView(section);
    initRestockButtons(section);
    watchThirdPartyWidgets(section);
  }

  function initAll(root = document) {
    root
      .querySelectorAll(
        '[data-pp-preorders]'
      )
      .forEach(initSection);
  }

  document.addEventListener(
    'DOMContentLoaded',
    () => initAll()
  );

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initAll(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      event.target
        .querySelectorAll(
          '[data-pp-countdown-wrap]'
        )
        .forEach((wrap) => {
          if (
            wrap._ppCountdownTimer
          ) {
            window.clearInterval(
              wrap._ppCountdownTimer
            );
          }
        });

      const section =
        event.target.querySelector(
          '[data-pp-preorders]'
        );

      if (
        section?._ppAutoplayTimer
      ) {
        window.clearInterval(
          section._ppAutoplayTimer
        );
      }

      if (
        section?._ppThirdPartyObserver
      ) {
        section._ppThirdPartyObserver.disconnect();
      }

      if (
        section?._ppVisibilityObserver
      ) {
        section._ppVisibilityObserver.disconnect();
      }

      if (
        section?._ppAnnouncementResizeObserver
      ) {
        section._ppAnnouncementResizeObserver.disconnect();
      }

      section
        ?.querySelectorAll(
          '[data-pp-products]'
        )
        .forEach((wrap) => {
          wrap._ppProductResizeObserver?.disconnect();
        });
    }
  );

  document.addEventListener(
    'shopify:block:select',
    (event) => {
      const slide =
        event.target.closest(
          '[data-pp-slide]'
        );

      const section =
        event.target.closest(
          '[data-pp-preorders]'
        );

      if (!slide || !section) {
        return;
      }

      const slides = Array.from(
        section.querySelectorAll(
          '[data-pp-slide]'
        )
      );

      const selectedIndex =
        slides.indexOf(slide);

      slides.forEach(
        (item, index) => {
          item.classList.toggle(
            'is-active',
            index ===
              selectedIndex
          );
        }
      );

      section
        .querySelectorAll(
          '[data-pp-dot]'
        )
        .forEach(
          (dot, index) => {
            dot.classList.toggle(
              'is-active',
              index ===
                selectedIndex
            );
          }
        );
    }
  );
})();